<script setup lang="ts">
import { Link, usePage } from "@inertiajs/vue3";
import {
  ChevronRightIcon,
  DownloadIcon,
  KeyRoundIcon,
  MailIcon,
  PaletteIcon,
  ShieldIcon,
  SmartphoneIcon,
  UserIcon,
} from "lucide-vue-next";
import { type Component, computed, ref } from "vue";

import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  organizationHelpPath,
  settingsApiKeysPath,
  settingsAppearancePath,
  settingsEmailPath,
  settingsPasswordPath,
  settingsProfilePath,
  settingsPwaPath,
  settingsSessionsPath,
} from "@/routes";
import type { SharedData } from "@/types";

const props = defineProps<{
  current:
    | "profile"
    | "email"
    | "password"
    | "sessions"
    | "api_keys"
    | "appearance"
    | "pwa";
  title: string;
  description: string;
}>();

const page = usePage<SharedData>();
const currentOrgId = computed(() => page.props.auth?.organization?.id);
const navDrawerOpen = ref(false);

interface NavItem {
  id: string;
  title: string;
  href: string;
  icon: Component;
}

const navItems = computed((): NavItem[] => [
  {
    id: "profile",
    title: "Perfil",
    href: settingsProfilePath(),
    icon: UserIcon,
  },
  {
    id: "email",
    title: "Email",
    href: settingsEmailPath(),
    icon: MailIcon,
  },
  {
    id: "password",
    title: "Password",
    href: settingsPasswordPath(),
    icon: ShieldIcon,
  },
  {
    id: "sessions",
    title: "Sesiones",
    href: settingsSessionsPath(),
    icon: SmartphoneIcon,
  },
  {
    id: "api_keys",
    title: "API keys",
    href: settingsApiKeysPath(),
    icon: KeyRoundIcon,
  },
  {
    id: "appearance",
    title: "Apariencia",
    href: settingsAppearancePath(),
    icon: PaletteIcon,
  },
  {
    id: "pwa",
    title: "PWA",
    href: settingsPwaPath(),
    icon: DownloadIcon,
  },
]);

const currentNavItem = computed(() =>
  navItems.value.find((item) => item.id === props.current),
);

const currentPath = computed(() => {
  const normalizedPath = page.url.replace(/\/$/, "");
  return normalizedPath.length > 0 ? normalizedPath : "/";
});

const isActive = (itemId: string, href: string) => {
  if (itemId === props.current) return true;
  const normalizedHref = href.replace(/\/$/, "") || "/";

  return (
    currentPath.value === normalizedHref ||
    currentPath.value.startsWith(`${normalizedHref}/`)
  );
};
</script>

<template>
  <!-- ═══ Mobile ═══ -->
  <section class="sm:hidden">
    <button
      class="mb-3 inline-flex items-center gap-2 rounded-full bg-muted/70 px-3 py-1.5 text-[13px] font-medium text-foreground transition active:scale-[0.97] active:bg-muted"
      @click="navDrawerOpen = true"
    >
      <component
        :is="currentNavItem?.icon"
        v-if="currentNavItem?.icon"
        class="size-3.5 text-muted-foreground"
      />
      {{ currentNavItem?.title ?? "Ajustes" }}
      <ChevronRightIcon class="size-3 text-muted-foreground" />
    </button>

    <header class="mb-4">
      <h1 class="text-[17px] font-semibold tracking-tight">
        {{ props.title }}
      </h1>
      <p class="mt-0.5 text-[13px] leading-snug text-muted-foreground">
        {{ props.description }}
      </p>
    </header>

    <div>
      <slot />
    </div>

    <Drawer v-model:open="navDrawerOpen">
      <DrawerContent>
        <DrawerHeader class="pb-2">
          <DrawerTitle class="text-base">Ajustes</DrawerTitle>
        </DrawerHeader>

        <nav class="flex flex-col gap-0.5 px-3 pb-8" aria-label="Secciones">
          <Link
            v-for="item in navItems"
            :key="item.id"
            :href="item.href"
            class="flex items-center gap-3 rounded-xl px-3 py-3 transition active:scale-[0.98]"
            :class="
              isActive(item.id, item.href)
                ? 'bg-foreground text-background'
                : 'text-foreground active:bg-muted'
            "
            @click="navDrawerOpen = false"
          >
            <span
              class="flex size-8 items-center justify-center rounded-lg"
              :class="
                isActive(item.id, item.href)
                  ? 'bg-background/15'
                  : 'bg-muted/70'
              "
            >
              <component
                :is="item.icon"
                class="size-4"
                :class="
                  isActive(item.id, item.href)
                    ? 'text-background'
                    : 'text-muted-foreground'
                "
              />
            </span>
            <span class="flex-1 text-[15px] font-medium">{{ item.title }}</span>
            <ChevronRightIcon
              v-if="!isActive(item.id, item.href)"
              class="size-4 text-muted-foreground/50"
            />
          </Link>

          <Link
            v-if="currentOrgId"
            :href="organizationHelpPath(currentOrgId)"
            class="mt-2 flex items-center justify-center rounded-xl border border-border px-3 py-2.5 text-[13px] font-medium text-muted-foreground transition active:scale-[0.98] active:bg-muted"
          >
            Ayuda
          </Link>
        </nav>
      </DrawerContent>
    </Drawer>
  </section>

  <!-- ═══ Desktop ═══ -->
  <div class="hidden min-h-0 flex-1 gap-3 sm:flex">
    <!-- Sidebar nav panel -->
    <aside
      class="flex w-[240px] min-w-[240px] flex-col overflow-hidden rounded-2xl border border-border bg-background"
    >
      <div class="px-5 pb-3 pt-5">
        <h3
          class="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70"
        >
          Cuenta
        </h3>
      </div>

      <nav
        class="flex flex-1 flex-col gap-0.5 overflow-y-auto px-2 pb-3 themed-scrollbar"
        aria-label="Settings sections"
      >
        <Link
          v-for="item in navItems"
          :key="item.id"
          :href="item.href"
          class="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm transition-colors"
          :class="
            isActive(item.id, item.href)
              ? 'bg-primary/[0.08] border-l-2 border-primary text-foreground'
              : 'text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground'
          "
        >
          <span
            class="flex size-7 shrink-0 items-center justify-center rounded-lg"
            :class="isActive(item.id, item.href) ? 'bg-primary/10' : 'bg-muted'"
          >
            <component
              :is="item.icon"
              class="size-3.5"
              :class="
                isActive(item.id, item.href)
                  ? 'text-primary'
                  : 'text-muted-foreground'
              "
            />
          </span>
          <span class="font-medium">{{ item.title }}</span>
        </Link>
      </nav>

      <div v-if="currentOrgId" class="border-t border-border px-3 py-3">
        <Link
          :href="organizationHelpPath(currentOrgId)"
          class="flex items-center justify-center rounded-lg px-3 py-2 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.03] hover:text-foreground"
        >
          Ayuda
        </Link>
      </div>
    </aside>

    <!-- Content panel -->
    <div
      class="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-2xl border border-border bg-background themed-scrollbar"
    >
      <header class="border-b border-border px-6 py-5">
        <h1 class="text-lg font-semibold tracking-tight">
          {{ props.title }}
        </h1>
        <p class="mt-0.5 text-sm text-muted-foreground">
          {{ props.description }}
        </p>
      </header>

      <div class="flex-1 px-6 py-5">
        <slot />
      </div>
    </div>
  </div>
</template>
